import {
  countableUnitLabels,
  isLeadNotice,
  separationNeedsCountableUnitMessage,
  separationNeedsUnitMessage,
  unitsTicket,
  type Currency,
  type PipelineStage,
  type QuoteItem,
  type UnitDto,
  type UnitType,
} from '@performance-crm/shared';
import {
  FINANCING_LABELS,
  FinancingType,
  InteractionType,
  UNIT_TYPE_LABELS,
  type StageMovePayload,
} from '@performance-crm/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UnitPicker } from '@/components/commercial/UnitPicker';
import { NextStepFields } from '@/components/leads/NextStepFields';
import {
  effectiveAppointmentAdvisor,
  emptyNextStep,
  toNextStep,
  toNextTask,
  type NextStepForm,
} from '@/components/leads/nextStep';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import {
  CURRENCY_SYMBOL,
  defaultNextTask,
  fmt,
  fromLocalInput,
  STAGE_LABEL,
  toLocalInput,
  TYPE_LABEL,
} from '@/lib/format';
import {
  emptyAppointment,
  toAppointmentDetails,
  type AppointmentForm,
} from '@/components/leads/appointment';
import { AvailabilityHint } from '@/components/leads/AvailabilityHint';
import { AppointmentFields } from '@/components/leads/AppointmentFields';
import { ContactDistrictField } from '@/components/leads/ContactDistrictField';
import {
  useCatalogs,
  useLead,
  useMoveStage,
  useProjects,
  useUnits,
  useUpdateContact,
} from '@/lib/queries';
import { useCompanyTimezone } from '@/lib/useCompanyTimezone';

const CONTACT_TYPES = [
  InteractionType.LLAMADA,
  InteractionType.WHATSAPP,
  InteractionType.MAIL,
  InteractionType.REUNION,
] as const;

/** Ayuda por etapa; la de Separación nombra los tipos contables de la empresa (P7). */
function stageHelp(stage: PipelineStage, countable: readonly UnitType[]): string | undefined {
  const help: Partial<Record<PipelineStage, string>> = {
    CONTACTADO: 'Registra el contacto efectivo con el cliente.',
    CITA: 'Agenda la cita: cuenta como cita generada en el mes programado.',
    VISITA: 'Registra la visita realizada (fecha de visita, no de registro).',
    SEPARACION: `Registra la separación con su fecha y al menos una unidad contable (${countableUnitLabels(countable)}); la cochera o el depósito se agregan además.`,
    VENTA:
      'Registra la venta (minuta firmada / contrato) de las unidades separadas y las que sumes. Cierra el lead como comprador.',
    PERDIDO: 'El lead se cierra con motivo; podrás reabrirlo después.',
  };
  return help[stage];
}

const CURRENCIES: Currency[] = ['USD', 'PEN'];

/** Marcador del selector de proforma para «ninguna» (la API recibe `quoteId: null`). */
const NO_QUOTE = 'NONE';

function CurrencySelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (v: Currency) => void;
  disabled?: boolean;
}) {
  return (
    <Select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as Currency)}
    >
      {CURRENCIES.map((c) => (
        <option key={c} value={c}>
          {CURRENCY_SYMBOL[c]}
        </option>
      ))}
    </Select>
  );
}

/**
 * Mover a Contactado, Cita, Visita, Separación, Venta o Perdido registrando el hecho. «Proforma» no pasa por aquí:
 * el tablero y la ficha abren el mismo `QuoteDialog` completo (Lote 5 · H2, P11).
 */
export function StageMoveDialog({
  leadId,
  companyId,
  projectId,
  stage,
  onOpenChange,
  onDone,
}: {
  leadId: string;
  companyId: string;
  /** Proyecto del lead: habilita el selector de unidades disponibles. */
  projectId?: string;
  stage: PipelineStage | null;
  onOpenChange: (o: boolean) => void;
  onDone?: () => void;
}) {
  const { data: catalogs } = useCatalogs(companyId);
  const tz = useCompanyTimezone(companyId);
  const { data: lead } = useLead(leadId);
  const { data: projects } = useProjects();
  const commercial = projects?.find((p) => p.id === projectId)?.commercial;
  const companyCurrency: Currency = catalogs?.settings.currency ?? 'USD';
  const penPerUsd = catalogs?.settings.penPerUsd ?? 3.75;
  const countableTypes = useMemo<UnitType[]>(
    () => catalogs?.settings.countableUnitTypes ?? ['DEPARTAMENTO', 'CASA'],
    [catalogs?.settings.countableUnitTypes],
  );
  const queryClient = useQueryClient();
  const mutation = useMoveStage(leadId);
  const updateContact = useUpdateContact(leadId);
  const toast = useToast();
  const navigate = useNavigate();
  const [district, setDistrict] = useState('');
  const [appointment, setAppointment] = useState<AppointmentForm>(emptyAppointment());
  const asksDistrict = stage === 'SEPARACION' || stage === 'VENTA';
  const [error, setError] = useState<string | null>(null);
  const [errorLink, setErrorLink] = useState<{ to: string; label: string } | null>(null);
  const [next, setNext] = useState<NextStepForm>(emptyNextStep(tz));
  const [unitIds, setUnitIds] = useState<string[]>([]);
  /** Unidades elegidas en el selector (con su moneda y precio); las separadas llegan por `separatedData`. */
  const [pickedUnits, setPickedUnits] = useState<UnitDto[]>([]);
  /** Último precio de venta sugerido: se reemplaza solo mientras la persona no lo cambió a mano. */
  const [autoTotal, setAutoTotal] = useState('');
  /** true si alguna unidad va en otra moneda y la suma se llevó a la del precio con el tipo de cambio. */
  const [autoTotalConverted, setAutoTotalConverted] = useState(false);
  const [f, setF] = useState({
    contactType: 'LLAMADA' as (typeof CONTACT_TYPES)[number],
    description: '',
    when: toLocalInput(new Date(), tz),
    future: defaultNextTask(tz),
    walkIn: false,
    currency: 'USD' as Currency,
    financing: '' as '' | FinancingType,
    amountInitial: '',
    initialCurrency: 'USD' as Currency,
    amountTotal: '',
    bank: '',
    contractNumber: '',
    reason: '',
    comment: '',
    /** '' = la más reciente de la unidad (automática); NO_QUOTE = ninguna; o el id elegido. */
    quoteId: '',
  });
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));

  // Separaciones vivas del lead: en Venta sus unidades entran siempre (P6) y la más reciente presta la reserva.
  const liveSeparations = useMemo(
    () => lead?.deals.filter((d) => d.type === 'SEPARACION' && d.state === 'ACTIVO') ?? [],
    [lead?.deals],
  );
  const latestSeparation = liveSeparations[0] ?? null;
  const separatedUnits = useMemo(() => {
    const seen = new Map<string, { id: string; code: string; price: string | null }>();
    for (const d of liveSeparations)
      for (const u of d.units) if (!seen.has(u.id)) seen.set(u.id, u);
    return [...seen.values()];
  }, [liveSeparations]);
  const separatedIds = useMemo(() => separatedUnits.map((u) => u.id), [separatedUnits]);
  const { data: separatedData } = useUnits(
    { projectId: projectId ?? '', ids: separatedIds },
    { enabled: stage === 'VENTA' && !!projectId && separatedIds.length > 0 },
  );
  const selectedUnits = useMemo<UnitDto[]>(
    () =>
      unitIds
        .map(
          (id) =>
            pickedUnits.find((u) => u.id === id) ?? separatedData?.items.find((u) => u.id === id),
        )
        .filter((u): u is UnitDto => !!u),
    [unitIds, pickedUnits, separatedData],
  );
  // Unidad principal: la primera contable (departamento, casa…) o la primera; manda la moneda del precio (P9).
  const mainUnit =
    selectedUnits.find((u) => countableTypes.includes(u.unitType)) ?? selectedUnits[0] ?? null;
  const priceCurrency: Currency =
    mainUnit?.currency ??
    (stage === 'VENTA' && latestSeparation ? (latestSeparation.currency as Currency) : f.currency);

  /**
   * Precio de venta sugerido: el pactado en la separación por unidad; para las que se suman, el de la unidad. Cada
   * precio va en la moneda de su unidad y la suma se expresa en la moneda del precio (la de la unidad principal)
   * con el tipo de cambio de la empresa (`unitsTicket`): US$ 150,000 + S/ 37,500 ya no da «187,500».
   */
  const suggestedTotal = (ids: string[], picked: UnitDto[]) => {
    const unitOf = (id: string) =>
      picked.find((u) => u.id === id) ?? separatedData?.items.find((u) => u.id === id);
    const chosen = ids.map(unitOf).filter((u): u is UnitDto => !!u);
    const main = chosen.find((u) => countableTypes.includes(u.unitType)) ?? chosen[0] ?? null;
    const to: Currency =
      main?.currency ?? (latestSeparation?.currency as Currency | undefined) ?? companyCurrency;
    const items = ids.map((id) => {
      const sep = separatedUnits.find((u) => u.id === id);
      const unit = unitOf(id);
      const price = sep?.price ?? unit?.priceSale ?? unit?.priceList ?? null;
      // Sin la unidad cargada aún, la moneda del precio pactado es la de su separación.
      const currency: Currency =
        unit?.currency ?? (latestSeparation?.currency as Currency | undefined) ?? to;
      return {
        price: price !== null && price !== undefined && Number(price) > 0 ? Number(price) : null,
        currency,
      };
    });
    const total = unitsTicket(items, to, penPerUsd);
    return {
      total: total === null ? '' : String(total),
      converted: items.some((i) => i.price !== null && i.currency !== to),
    };
  };

  // Lo que cambia el prellenado del formulario: solo cuando estos datos cambian de verdad (no en cada refetch).
  const prefillKey = [
    commercial?.reservationAmount ?? '',
    commercial?.reservationCurrency ?? '',
    companyCurrency,
    separatedIds.join(','),
    latestSeparation?.id ?? '',
    latestSeparation?.amountInitial ?? '',
    latestSeparation?.initialCurrency ?? '',
  ].join('|');

  useEffect(() => {
    if (stage) setDistrict(lead?.contact.district ?? '');
    if (stage === 'CITA') setAppointment(emptyAppointment());
  }, [stage, lead?.contact.district]);
  // Un solo reinicio al abrir (P10): limpia todo y, dentro del mismo paso, prellena lo que corresponde a la etapa
  // (monto y moneda de reserva del proyecto en Separación; unidades, inicial, moneda y financiamiento de la
  // separación en Venta). Antes el prellenado y el reinicio corrían por separado y el monto quedaba vacío.
  useEffect(() => {
    if (!stage) return;
    setError(null);
    setErrorLink(null);
    setNext(emptyNextStep(tz));
    setPickedUnits([]);
    const ids = stage === 'VENTA' ? separatedIds : [];
    setUnitIds(ids);
    const suggested = stage === 'VENTA' ? suggestedTotal(ids, []) : { total: '', converted: false };
    const total = suggested.total;
    setAutoTotal(total);
    setAutoTotalConverted(suggested.converted);
    setF((p) => ({
      ...p,
      description: '',
      when: toLocalInput(new Date(), tz),
      future: defaultNextTask(tz),
      walkIn: false,
      currency:
        stage === 'VENTA' && latestSeparation
          ? (latestSeparation.currency as Currency)
          : companyCurrency,
      financing:
        stage === 'VENTA' ? ((latestSeparation?.contractMode as FinancingType | null) ?? '') : '',
      amountInitial:
        stage === 'SEPARACION'
          ? commercial?.reservationAmount
            ? String(commercial.reservationAmount)
            : ''
          : stage === 'VENTA' && latestSeparation?.amountInitial
            ? String(Number(latestSeparation.amountInitial))
            : '',
      initialCurrency:
        stage === 'SEPARACION'
          ? (commercial?.reservationCurrency ?? companyCurrency)
          : stage === 'VENTA'
            ? ((latestSeparation?.initialCurrency as Currency | undefined) ?? companyCurrency)
            : companyCurrency,
      amountTotal: total,
      bank: stage === 'VENTA' ? (latestSeparation?.bank ?? '') : '',
      contractNumber: '',
      reason: '',
      comment: '',
      quoteId: '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, prefillKey, tz]);

  // Proformas que se pueden convertir (P8): vigentes o vencidas de una de las unidades elegidas.
  const convertibleQuotes = useMemo<QuoteItem[]>(
    () =>
      (lead?.quotes ?? []).filter(
        (q) =>
          (q.status === 'VIGENTE' || q.status === 'EXPIRADA') &&
          !!q.unitId &&
          unitIds.includes(q.unitId),
      ),
    [lead?.quotes, unitIds],
  );
  const defaultQuote =
    convertibleQuotes.find((q) => q.unitId === mainUnit?.id) ?? convertibleQuotes[0] ?? null;
  const quoteValue =
    f.quoteId && (f.quoteId === NO_QUOTE || convertibleQuotes.some((q) => q.id === f.quoteId))
      ? f.quoteId
      : (defaultQuote?.id ?? NO_QUOTE);
  // En Venta, la separación con proforma manda: esa es la que se usa (no se elige otra).
  const separationQuoteCode = stage === 'VENTA' ? (latestSeparation?.quoteCode ?? null) : null;

  if (!stage || stage === 'PROFORMA') return null;
  const onUnits = (ids: string[], units: UnitDto[]) => {
    // Las unidades separadas por el cliente no se pueden quitar de la venta (la API las suma igual).
    const next = stage === 'VENTA' ? [...new Set([...separatedIds, ...ids])] : ids;
    setUnitIds(next);
    setPickedUnits(units);
    if (stage === 'VENTA') {
      const { total, converted } = suggestedTotal(next, units);
      if (!f.amountTotal || f.amountTotal === autoTotal) set({ amountTotal: total });
      setAutoTotal(total);
      setAutoTotalConverted(converted);
    }
  };
  const needsNext = stage === 'CONTACTADO' || stage === 'VISITA' || stage === 'SEPARACION';
  // `null` = ninguna (la API no convierte nada); un id = esa proforma (validada contra unidad y estado).
  const quoteIdPayload = (): string | null => (quoteValue === NO_QUOTE ? null : quoteValue);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorLink(null);
    let payload: StageMovePayload;
    switch (stage) {
      case 'CONTACTADO':
        payload = {
          stage,
          contact: { type: f.contactType, description: f.description || null },
          next: toNextStep(next, tz),
        };
        break;
      case 'CITA':
        payload = {
          stage,
          appointment: {
            scheduledAt: new Date(fromLocalInput(f.future, tz)),
            description: f.description || null,
            details: toAppointmentDetails(appointment),
          },
        };
        break;
      case 'VISITA':
        payload = {
          stage,
          visit: {
            visitAt: new Date(fromLocalInput(f.when, tz)),
            description: f.description || null,
            walkIn: f.walkIn,
          },
          next: toNextStep(next, tz),
        };
        break;
      case 'SEPARACION': {
        if (unitIds.length === 0) {
          setError(separationNeedsUnitMessage(countableTypes));
          return;
        }
        if (
          selectedUnits.length === unitIds.length &&
          !selectedUnits.some((u) => countableTypes.includes(u.unitType))
        ) {
          setError(separationNeedsCountableUnitMessage(countableTypes));
          return;
        }
        payload = {
          stage,
          deal: {
            startedAt: new Date(fromLocalInput(f.when, tz)),
            unitIds,
            quoteId: quoteIdPayload(),
            amountInitial: f.amountInitial ? Number(f.amountInitial) : null,
            initialCurrency: f.initialCurrency,
            // La separación no tiene campo de precio: la moneda del precio es la de la unidad (la pone la API).
            currency: mainUnit?.currency ?? null,
            contractMode: f.financing || null,
            bank: f.bank || null,
            description: f.description || null,
          },
          // Con la separación recién registrada no se desiste: siempre una tarea (Lote 5 · H1).
          next: toNextTask(next, tz),
        };
        break;
      }
      case 'VENTA':
        if (unitIds.length === 0) {
          setError('Elige al menos una unidad para registrar la venta');
          return;
        }
        payload = {
          stage,
          deal: {
            finishedAt: new Date(fromLocalInput(f.when, tz)),
            unitIds,
            ...(separationQuoteCode ? {} : { quoteId: quoteIdPayload() }),
            amountTotal: f.amountTotal ? Number(f.amountTotal) : null,
            amountInitial: f.amountInitial ? Number(f.amountInitial) : null,
            initialCurrency: f.initialCurrency,
            currency: priceCurrency,
            contractMode: f.financing || null,
            bank: f.bank || null,
            contractNumber: f.contractNumber || null,
            description: f.description || null,
          },
        };
        break;
      case 'PERDIDO':
        payload = { stage, desist: { reason: f.reason, comment: f.comment || null } };
        break;
      default:
        return;
    }
    try {
      // El distrito vive en el contacto: se guarda antes del hecho comercial si cambió.
      if (asksDistrict && lead && district.trim() !== (lead.contact.district ?? ''))
        await updateContact.mutateAsync({
          contactId: lead.contact.id,
          input: { district: district.trim() || null },
        });
      const result = await mutation.mutateAsync(payload);
      onDone?.();
      onOpenChange(false);
      if (isLeadNotice(result)) {
        // «Mover a Visita» concretó la cita y el lead pasó al asesor de sala: ya no es de quien lo movió (E1).
        toast.success(result.message);
        navigate('/');
        return;
      }
      toast.success(`Lead movido a ${STAGE_LABEL[stage]}`);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'No se pudo mover el lead');
      // Otra persona tiene la unidad (P5): si quien registra puede ver ese lead, se enlaza.
      const details =
        err instanceof ApiRequestError
          ? (err.body.details as
              { code?: string; holderLeadId?: string; holderVisible?: boolean } | undefined)
          : undefined;
      if (details?.code === 'UNIT_TAKEN') {
        // El selector listaba la unidad como disponible: se vuelve a pedir el stock para que desaparezca.
        void queryClient.invalidateQueries({ queryKey: ['units'] });
        if (details.holderVisible && details.holderLeadId)
          setErrorLink({ to: `/leads/${details.holderLeadId}`, label: 'Ver el lead que la tiene' });
      }
    }
  };

  const unitsLabel =
    stage === 'SEPARACION'
      ? `Unidades separadas (al menos una unidad: ${countableUnitLabels(countableTypes)}) *`
      : 'Unidades vendidas';

  return (
    <Dialog open={!!stage} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Mover a {STAGE_LABEL[stage]}</DialogTitle>
            <DialogDescription>{stageHelp(stage, countableTypes)}</DialogDescription>
          </DialogHeader>

          {stage === 'CONTACTADO' && (
            <div className="space-y-1">
              <Label htmlFor="mv-type">Tipo de contacto</Label>
              <Select
                id="mv-type"
                value={f.contactType}
                onChange={(e) =>
                  set({ contactType: e.target.value as (typeof CONTACT_TYPES)[number] })
                }
              >
                {CONTACT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </div>
          )}
          {stage === 'CITA' && (
            <>
              <div className="space-y-1">
                <Label htmlFor="mv-future">Fecha y hora de la cita</Label>
                <Input
                  id="mv-future"
                  type="datetime-local"
                  value={f.future}
                  onChange={(e) => set({ future: e.target.value })}
                  required
                />
                {lead && (
                  <AvailabilityHint
                    companyId={companyId}
                    projectId={projectId}
                    advisorId={effectiveAppointmentAdvisor(appointment, lead.advisorId)}
                    advisorLabel={appointment.roomAdvisorId ? 'el asesor de sala' : 'el asesor'}
                    at={f.future}
                  />
                )}
              </div>
              <AppointmentFields
                value={appointment}
                onChange={setAppointment}
                projectId={projectId}
                companyId={companyId}
                idPrefix="mv-appt"
              />
            </>
          )}
          {(stage === 'VISITA' || stage === 'SEPARACION' || stage === 'VENTA') && (
            <div className="space-y-1">
              <Label htmlFor="mv-when">
                {stage === 'VISITA'
                  ? 'Fecha de la visita'
                  : stage === 'SEPARACION'
                    ? 'Fecha de separación'
                    : 'Fecha de venta'}
              </Label>
              <Input
                id="mv-when"
                type="datetime-local"
                value={f.when}
                max={toLocalInput(new Date(), tz)}
                onChange={(e) => set({ when: e.target.value })}
                required
              />
              <p className="text-muted-foreground text-xs">
                Hora de {tz === 'America/Lima' ? 'Lima' : tz}; nunca a futuro.
              </p>
            </div>
          )}
          {stage === 'VISITA' && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={f.walkIn}
                onChange={(e) => set({ walkIn: e.target.checked })}
                className="h-4 w-4"
              />{' '}
              Llegó sin cita (tránsito)
            </label>
          )}
          {(stage === 'SEPARACION' || stage === 'VENTA') && projectId && (
            <div className="space-y-1">
              <Label>{unitsLabel}</Label>
              {stage === 'VENTA' && separatedUnits.length > 0 && (
                <p className="text-sm" data-testid="mv-sale-units">
                  Se venderán: <strong>{separatedUnits.map((u) => u.code).join(', ')}</strong>{' '}
                  <span className="text-muted-foreground">
                    (separadas por el cliente) · lo que marques además se suma
                  </span>
                </p>
              )}
              {stage === 'VENTA' && separatedUnits.length === 0 && (
                <p className="text-muted-foreground text-xs">
                  Este lead no tiene separación vigente: marca la unidad que se vende.
                </p>
              )}
              <UnitPicker
                projectId={projectId}
                value={unitIds}
                onChange={onUnits}
                multiple
                idPrefix={`mv-${stage.toLowerCase()}`}
                extraUnitIds={stage === 'VENTA' ? separatedIds : []}
                lockedIds={stage === 'VENTA' ? separatedIds : []}
              />
              {stage === 'SEPARACION' &&
                selectedUnits.length > 0 &&
                !selectedUnits.some((u) => countableTypes.includes(u.unitType)) && (
                  <p className="text-warning text-xs" data-testid="mv-countable-hint">
                    Solo marcaste{' '}
                    {selectedUnits
                      .map((u) => (UNIT_TYPE_LABELS[u.unitType] ?? u.unitType).toLowerCase())
                      .join(', ')}
                    : agrega la unidad que se separa ({countableUnitLabels(countableTypes)}).
                  </p>
                )}
            </div>
          )}
          {asksDistrict && (
            <ContactDistrictField id="mv-district" value={district} onChange={setDistrict} />
          )}
          {(stage === 'SEPARACION' || stage === 'VENTA') && (
            <div className="grid gap-3 sm:grid-cols-3">
              {stage === 'VENTA' && (
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="mv-total">Precio de venta</Label>
                  <Input
                    id="mv-total"
                    type="number"
                    min={0}
                    step="0.01"
                    value={f.amountTotal}
                    onChange={(e) => set({ amountTotal: e.target.value })}
                  />
                  {selectedUnits.length > 1 && autoTotal && (
                    <p className="text-muted-foreground text-xs" data-testid="mv-sale-sum">
                      Suma de las unidades
                      {autoTotalConverted ? ', al tipo de cambio de la empresa' : ''}:{' '}
                      {fmt.money(autoTotal, priceCurrency)}
                    </p>
                  )}
                </div>
              )}
              {(convertibleQuotes.length > 0 || separationQuoteCode) && (
                // P8: solo las proformas vigentes o vencidas de las unidades elegidas; en Venta manda la de la
                // separación (no se elige otra).
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="mv-quote">
                    {stage === 'SEPARACION' ? 'Proforma que se convierte' : 'Proforma'}
                  </Label>
                  {separationQuoteCode ? (
                    <p className="flex h-10 items-center text-sm" data-testid="mv-sale-quote">
                      {separationQuoteCode}{' '}
                      <span className="text-muted-foreground ml-1">(la de la separación)</span>
                    </p>
                  ) : (
                    <>
                      <Select
                        id="mv-quote"
                        value={quoteValue}
                        onChange={(e) => set({ quoteId: e.target.value })}
                      >
                        <option value={NO_QUOTE}>Sin proforma (no se convierte ninguna)</option>
                        {convertibleQuotes.map((q) => (
                          <option key={q.id} value={q.id}>
                            {q.code} · {q.unitCode} · {fmt.money(q.price, q.currency)}
                            {q.status === 'EXPIRADA' ? ' · vencida' : ''}
                          </option>
                        ))}
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        Solo las vigentes o vencidas de las unidades elegidas; el precio pactado
                        pasa al proceso.
                      </p>
                    </>
                  )}
                </div>
              )}
              {stage === 'VENTA' && (
                // Solo donde hay campo de precio: en Separación la moneda del precio es la de la unidad y no se
                // muestra (la reserva tiene la suya).
                <div className="space-y-1">
                  <Label htmlFor="mv-currency">Moneda del precio</Label>
                  <CurrencySelect
                    id="mv-currency"
                    value={priceCurrency}
                    disabled={!!mainUnit || !!latestSeparation}
                    onChange={(c) => set({ currency: c })}
                  />
                  {(mainUnit || latestSeparation) && (
                    <p className="text-muted-foreground text-xs">La de la unidad.</p>
                  )}
                </div>
              )}
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="mv-initial">
                  {stage === 'SEPARACION' ? 'Monto de separación' : 'Inicial (de la reserva)'}
                </Label>
                <Input
                  id="mv-initial"
                  type="number"
                  min={0}
                  step="0.01"
                  value={f.amountInitial}
                  onChange={(e) => set({ amountInitial: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mv-initial-currency">
                  {stage === 'SEPARACION' ? 'Moneda de la reserva' : 'Moneda de la inicial'}
                </Label>
                <CurrencySelect
                  id="mv-initial-currency"
                  value={f.initialCurrency}
                  onChange={(c) => set({ initialCurrency: c })}
                />
              </div>
              <div className="space-y-1 sm:col-span-3">
                <Label htmlFor="mv-fin">Financiamiento</Label>
                <Select
                  id="mv-fin"
                  value={f.financing}
                  onChange={(e) => set({ financing: e.target.value as '' | FinancingType })}
                >
                  <option value="">—</option>
                  {Object.values(FinancingType).map((v) => (
                    <option key={v} value={v}>
                      {FINANCING_LABELS[v]}
                    </option>
                  ))}
                </Select>
              </div>
              {stage === 'SEPARACION' && (
                <div className="space-y-1 sm:col-span-3">
                  <Label htmlFor="mv-sep-bank">Banco (si aplica)</Label>
                  <Input
                    id="mv-sep-bank"
                    value={f.bank}
                    onChange={(e) => set({ bank: e.target.value })}
                  />
                </div>
              )}
              {stage === 'VENTA' && (
                <>
                  <div className="space-y-1 sm:col-span-2">
                    <Label htmlFor="mv-bank">Banco</Label>
                    <Input
                      id="mv-bank"
                      value={f.bank}
                      onChange={(e) => set({ bank: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="mv-contract">N° contrato</Label>
                    <Input
                      id="mv-contract"
                      value={f.contractNumber}
                      onChange={(e) => set({ contractNumber: e.target.value })}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          {stage === 'PERDIDO' && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="mv-reason">Motivo</Label>
                <Select
                  id="mv-reason"
                  value={f.reason}
                  onChange={(e) => set({ reason: e.target.value })}
                  required
                >
                  <option value="">Selecciona…</option>
                  {catalogs?.desistReasons.map((r) => (
                    <option key={r.code} value={r.code}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="mv-comment">Comentario</Label>
                <Textarea
                  id="mv-comment"
                  value={f.comment}
                  onChange={(e) => set({ comment: e.target.value })}
                  rows={2}
                />
              </div>
            </div>
          )}
          {stage !== 'PERDIDO' && (
            <div className="space-y-1">
              <Label htmlFor="mv-desc">Detalle</Label>
              <Textarea
                id="mv-desc"
                value={f.description}
                onChange={(e) => set({ description: e.target.value })}
                rows={2}
                placeholder={
                  stage === 'VENTA' ? 'Condiciones de la minuta, acuerdos… (queda como nota)' : ''
                }
              />
            </div>
          )}
          {needsNext && (
            // Al registrar una separación el lead queda con un proceso vivo: «Desistir» como siguiente paso ya no
            // tiene sentido (la API lo rechaza; primero se devuelve o anula). Solo se programa la siguiente tarea.
            <NextStepFields
              availability={lead ? { companyId, projectId, advisorId: lead.advisorId } : undefined}
              value={next}
              onChange={setNext}
              desistReasons={catalogs?.desistReasons ?? []}
              idPrefix="mv-next"
              allowDesist={stage !== 'SEPARACION'}
            />
          )}
          {error && (
            <p
              role="alert"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {error}
              {errorLink && (
                <>
                  {' '}
                  <Link to={errorLink.to} className="underline" onClick={() => onOpenChange(false)}>
                    {errorLink.label}
                  </Link>
                </>
              )}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Mover
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
